<script setup lang="ts">
import { ref } from 'vue'
import { useRoute } from 'vue-router'
import { useDark } from '@vueuse/core'

import Drawer from 'primevue/drawer'

import { ChevronDown } from 'lucide-vue-next'
import Logo from '@/assets/svg/logo.svg'
import Nebula from '@/assets/svg/nebula.svg'
import AppButton from '@/components/atoms/buttons/AppButton.vue'

import type { IRoute } from '../interfaces/route.interface'

defineProps<{
  visible: boolean
  routes: IRoute[]
}>()

defineEmits<{
  'update:visible': [value: boolean]
  close: []
}>()

const currentRoute = useRoute()
const isDark = useDark({ selector: 'html' })
const openSubmenus = ref<Set<string>>(new Set())

const toggleSubmenu = (routePath: string) => {
  if (openSubmenus.value.has(routePath)) {
    openSubmenus.value.delete(routePath)
  } else {
    openSubmenus.value.add(routePath)
  }
}

const isRouteActive = (route: IRoute) => {
  const currentPath = currentRoute.path
  if (route.children) {
    return route.children.some((child) => currentPath === child.path)
  }
  return currentPath === route.path
}

const isChildActive = (childPath: string) => {
  return currentRoute.path === childPath
}
</script>

<template>
  <Drawer
    :visible="visible"
    @update:visible="$emit('update:visible', $event)"
    :modal="true"
    position="left"
    style="width: min(18rem, calc(100vw - 2rem))"
  >
    <template #container="{ closeCallback }">
      <div
        class="app-glass flex h-full flex-col border border-white/70 text-slate-800 transition-colors duration-300 dark:border-white/10 dark:text-slate-100"
        :class="isDark ? 'app-glass--dark' : ''"
      >
        <!-- Header con logo -->
        <div class="flex items-center justify-between px-3 py-2">
          <div class="w-10"></div>
          <router-link
            to="/"
            @click="closeCallback"
            class="flex items-center flex-1 justify-center"
          >
            <Logo
              :class="
                isDark
                  ? 'h-16 w-16 transition-all duration-500 ease-in-out text-(--p-primary-color)'
                  : 'h-16 w-16 transition-all duration-500 ease-in-out text-(--p-primary-color)'
              "
            />
          </router-link>
          <div class="w-10 flex justify-end">
            <AppButton
              @click="closeCallback"
              icon="pi pi-times"
              size="small"
              text
              rounded
              :class="
                isDark
                  ? '!text-slate-300 hover:!text-white'
                  : '!text-slate-500 hover:!text-slate-900'
              "
            />
          </div>
        </div>

        <!-- Menu items -->
        <div class="flex-1 overflow-y-auto px-2 py-3">
          <ul class="w-full space-y-1">
            <li v-for="route in routes" :key="route.path" class="w-full">
              <template v-if="!route.children">
                <!-- Item sin hijos -->
                <router-link
                  :to="route.path"
                  @click="closeCallback"
                  class="group mb-1 flex items-center gap-3 rounded-lg border border-transparent p-2.5 transition-colors duration-150"
                  :class="
                    isRouteActive(route)
                      ? isDark
                        ? 'border-(--p-primary-color)/20 bg-(--p-primary-color)/10'
                        : 'border-(--p-primary-color)/20 bg-(--p-primary-color)/10'
                      : isDark
                        ? 'hover:bg-white/5'
                        : 'hover:bg-slate-900/5'
                  "
                >
                  <component
                    :is="route.icon"
                    :size="20"
                    :stroke-width="1.5"
                    class="shrink-0 transition-colors duration-200"
                    :class="
                      isRouteActive(route)
                        ? isDark
                          ? 'text-(--p-primary-color)'
                          : 'text-(--p-primary-color)'
                        : isDark
                          ? 'text-(--p-primary-color) group-hover:text-(--p-primary-color)'
                          : 'text-slate-500 group-hover:text-(--p-primary-color)'
                    "
                  />
                  <span
                    class="text-sm font-medium transition-all duration-200"
                    :class="
                      isRouteActive(route)
                        ? isDark
                          ? 'text-(--p-primary-color)'
                          : 'text-(--p-primary-color)'
                        : isDark
                          ? 'text-slate-300 group-hover:text-white'
                          : 'text-slate-600 group-hover:text-(--p-primary-color)'
                    "
                  >
                    {{ route.title }}
                  </span>
                </router-link>
                <hr
                  class="mx-auto w-8/9 border-0 border-b border-b-slate-200/70 dark:border-b-white/10"
                />
              </template>

              <!-- Item con hijos (submenu) -->
              <div v-else class="w-full">
                <button
                  @click="toggleSubmenu(route.path)"
                  class="group flex w-full items-center gap-3 rounded-lg border border-transparent p-2.5 transition-colors duration-150"
                  :class="
                    isRouteActive(route)
                      ? isDark
                        ? 'border-(--p-primary-color)/20 bg-(--p-primary-color)/10'
                        : 'border-(--p-primary-color)/20 bg-(--p-primary-color)/10'
                      : isDark
                        ? 'hover:bg-white/5'
                        : 'hover:bg-slate-900/5'
                  "
                >
                  <component
                    :is="route.icon"
                    :size="20"
                    :stroke-width="2"
                    class="shrink-0 transition-colors duration-200"
                    :class="
                      isRouteActive(route)
                        ? isDark
                          ? 'text-(--p-primary-color)'
                          : 'text-(--p-primary-color)'
                        : isDark
                          ? 'text-slate-300 group-hover:text-white'
                          : 'text-slate-600 group-hover:text-(--p-primary-color)'
                    "
                  />
                  <span
                    class="text-sm font-medium flex-1 text-left transition-all duration-200"
                    :class="
                      isRouteActive(route)
                        ? isDark
                          ? 'text-(--p-primary-color)'
                          : 'text-(--p-primary-color)'
                        : isDark
                          ? 'text-(--p-primary-color) group-hover:text-(--p-primary-color)'
                          : 'text-white/90 group-hover:text-white'
                    "
                  >
                    {{ route.title }}
                  </span>
                  <ChevronDown
                    :size="16"
                    class="transition-all duration-200"
                    :class="[
                      { 'rotate-180': openSubmenus.has(route.path) },
                      isRouteActive(route)
                        ? isDark
                          ? 'text-(--p-surface-900)'
                          : 'text-(--p-primary-color)'
                        : isDark
                          ? 'text-slate-400'
                          : 'text-slate-400',
                    ]"
                  />
                </button>

                <!-- Submenu -->
                <div class="overflow-hidden">
                  <transition
                    enter-active-class="transition-all duration-200 ease-out"
                    leave-active-class="transition-all duration-150 ease-in"
                    enter-from-class="opacity-0 max-h-0"
                    enter-to-class="opacity-100 max-h-96"
                    leave-from-class="opacity-100 max-h-96"
                    leave-to-class="opacity-0 max-h-0"
                  >
                    <ul v-show="openSubmenus.has(route.path)" class="mt-1 ml-8 space-y-1">
                      <li v-for="child in route.children ?? []" :key="child.path">
                        <router-link
                          :to="child.path"
                          @click="closeCallback"
                          class="group flex items-center gap-2 p-2 pl-3 rounded-md transition-all duration-200"
                          :class="
                            isChildActive(child.path)
                              ? isDark
                                ? 'bg-(--p-primary-color)/10 text-(--p-primary-color)'
                                : 'bg-(--p-primary-color)/10 text-(--p-primary-color)'
                              : isDark
                                ? 'text-slate-300 hover:bg-white/5 hover:text-white'
                                : 'text-slate-500 hover:bg-slate-900/5 hover:text-slate-900'
                          "
                        >
                          <component
                            :is="child.icon"
                            v-if="child.icon"
                            :size="14"
                            :stroke-width="2"
                            class="shrink-0 transition-colors duration-200"
                          />
                          <div
                            v-else
                            class="w-1.5 h-1.5 rounded-full transition-colors duration-200"
                            :class="
                              isChildActive(child.path)
                                ? isDark
                                  ? 'bg-(--p-primary-color)'
                                  : 'bg-(--p-primary-color)'
                                : isDark
                                  ? 'bg-slate-500 group-hover:bg-white'
                                  : 'bg-slate-400 group-hover:bg-(--p-primary-color)'
                            "
                          ></div>
                          <span class="text-xs font-medium">{{ child.title }}</span>
                        </router-link>
                      </li>
                    </ul>
                  </transition>
                </div>
              </div>
            </li>
          </ul>
        </div>

        <!-- Logo Nebula -->
        <div class="relative mt-auto">
          <div class="flex items-center justify-center pb-4 px-2">
            <Nebula
              class="h-4 w-auto transition-all duration-300"
              :class="isDark ? 'text-(--p-primary-color)' : 'text-(--p-primary-color)'"
            />
          </div>
        </div>
      </div>
    </template>
  </Drawer>
</template>
